import { HttpErrorResponse } from '@angular/common/http';

/**
 * How often a firm is invoiced.
 *
 * Credits are granted per calendar month either way — the interval changes only the billing rhythm and
 * which price each product is booked at. Treated as a property of the whole firm rather than of a
 * single seat: Stripe's flexible billing mode does permit items on different intervals, but a plan
 * whose seats and add-ons renew on different dates has no single answer to "when do you next pay".
 */
export type BillingInterval = 'month' | 'year';

/** Every subscription starts monthly; yearly is chosen deliberately from the billing settings. */
export const DEFAULT_BILLING_INTERVAL: BillingInterval = 'month';

/** Narrows the generated `Price.interval` string, which is also `null` for one-off prices. */
export function toBillingInterval(value: string | null | undefined): BillingInterval | undefined {
  return value === 'month' || value === 'year' ? value : undefined;
}

/**
 * How much cheaper a year is than twelve months, as a whole percent.
 *
 * Both totals are in cents, as the billing context reports them. Nothing is advertised when the
 * catalogue has no real saving — a zero or missing discount is not a percentage.
 */
export function yearlySavingsPercent(
  monthlyTotal: number | null | undefined,
  yearlyTotal: number | null | undefined,
): number | null {
  if (monthlyTotal == null || yearlyTotal == null || monthlyTotal <= 0) return null;
  const yearAtMonthly = monthlyTotal * 12;
  const savings = yearAtMonthly - yearlyTotal;
  if (savings <= 0) return null;
  return Math.round((savings / yearAtMonthly) * 100);
}

/**
 * VAT on a yearly-switch invoice, in cents.
 *
 * Stripe's `due_today` is the invoice `total`, which includes German VAT. The yearly catalogue total
 * and the unused-month credit are net, so without this figure the confirmation dialog's lines cannot
 * add up to what is actually charged. The backend's `tax` wins; the difference is only a fallback for
 * an older backend, as it also holds any pending prorations billed on the same invoice.
 */
export function invoiceVatCents(
  recurringTotal: number | null | undefined,
  prorationCredit: number | null | undefined,
  dueToday: number | null | undefined,
  tax?: number | null,
): number | null {
  if (tax != null) return tax > 0 ? tax : null;
  if (recurringTotal == null || dueToday == null) return null;
  const vat = dueToday - (recurringTotal + (prorationCredit ?? 0));
  return vat > 0 ? vat : null;
}

/**
 * Net pending seat and add-on changes billed on the yearly-switch invoice, in cents: what `due_today`
 * holds beyond the yearly price, the credit and VAT. Null without the backend's `tax` or when none.
 */
export function pendingChangesCents(
  recurringTotal: number | null | undefined,
  prorationCredit: number | null | undefined,
  dueToday: number | null | undefined,
  tax: number | null | undefined,
): number | null {
  if (recurringTotal == null || dueToday == null || tax == null) return null;
  const pending = dueToday - tax - (recurringTotal + (prorationCredit ?? 0));
  return pending !== 0 ? pending : null;
}

/**
 * i18n key for the recurrence word of `value`.
 */
export function billingPeriodLabelKey(value: string | null | undefined): string {
  return toBillingInterval(value) === 'year' ? 'common.period.year' : 'common.period.month';
}

/** Sent by the backend while the users' prices have not yet followed an interval switch. */
const BILLING_SYNC_PENDING_CODE = 'billing_sync_pending';

/**
 * Whether a seat or add-on change was refused because an interval switch is still being applied.
 *
 * Its webhook moves the users onto the new prices within seconds; until then a save would put the old
 * interval's items back, so the backend asks to try again rather than failing for good.
 */
export function isBillingSyncPending(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 409 && error.error?.code === BILLING_SYNC_PENDING_CODE;
}

/** Sent by the backend when switching back to monthly is refused on a subscription set to cancel. */
const SUBSCRIPTION_CANCELLING_CODE = 'subscription_cancelling';

/**
 * Whether a switch back to monthly (or keeping yearly) was refused because the subscription is set to cancel.
 *
 * Stripe cannot schedule the monthly phase on a subscription that ends, so the cancellation has to be
 * reverted first; switching to yearly is not affected.
 */
export function isSubscriptionCancelling(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 409 && error.error?.code === SUBSCRIPTION_CANCELLING_CODE;
}
